
import React, { useState } from 'react';
import { useApiProviders } from '@/hooks/useApiProviders';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import { EndpointManager } from './EndpointManager';
import { ProviderForm } from './ProviderForm';
import { useToast } from '@/hooks/use-toast';
import { 
  Database, 
  Plus, 
  MoreVertical, 
  Pencil, 
  ListTree,
  RefreshCw,
  Code
} from 'lucide-react';
import { ensure24SevenProvider } from '@/utils/setup24SevenProvider';

export function ProviderManager() {
  const { data: providers = [], isLoading, refetch } = useApiProviders();
  const [activeTab, setActiveTab] = useState<'list' | 'add' | 'endpoints'>('list');
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState<any>(null);
  const [selectedProviderId, setSelectedProviderId] = useState<string | null>(null);
  const { toast } = useToast();
  const [isSetupLoading, setIsSetupLoading] = useState(false);
  
  const handleAddProvider = () => {
    setSelectedProvider(null);
    setIsAddDialogOpen(true);
  };
  
  const handleEditProvider = (provider: any) => {
    setSelectedProvider(provider);
    setIsAddDialogOpen(true);
  };
  
  const handleManageEndpoints = (providerId: string) => {
    setSelectedProviderId(providerId);
    setActiveTab('endpoints');
  };
  
  const handleCloseDialog = () => {
    setIsAddDialogOpen(false);
  };
  
  const handleSetup24Seven = async () => {
    setIsSetupLoading(true);
    try {
      const provider = await ensure24SevenProvider();
      toast({
        title: "24Seven Office Setup",
        description: "24Seven Office provider has been configured successfully.",
      });
      await refetch();
      handleManageEndpoints(provider.id);
    } catch (error) {
      console.error("24Seven setup error:", error);
      toast({
        variant: "destructive",
        title: "Setup failed",
        description: error instanceof Error ? error.message : "Failed to set up 24Seven provider.",
      });
    } finally {
      setIsSetupLoading(false);
    }
  };
  
  return (
    <div className="space-y-6">
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'list' | 'add' | 'endpoints')}>
        <div className="flex justify-between items-center mb-6">
          <TabsList>
            <TabsTrigger value="list">Providers</TabsTrigger>
            <TabsTrigger value="add">Add Provider</TabsTrigger>
            {selectedProviderId && <TabsTrigger value="endpoints">Endpoints</TabsTrigger>}
          </TabsList>
          
          <div className="flex gap-2">
            <Button onClick={handleSetup24Seven} disabled={isSetupLoading} variant="outline">
              {isSetupLoading ? <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> : <Code className="mr-2 h-4 w-4" />}
              Setup 24Seven Office
            </Button>
            <Button onClick={handleAddProvider}>
              <Plus className="mr-2 h-4 w-4" />
              Add Provider
            </Button>
          </div>
        </div>
        
        <TabsContent value="list">
          {isLoading ? (
            <div>Loading providers...</div>
          ) : providers.length === 0 ? (
            <Card className="border border-dashed">
              <CardHeader>
                <CardTitle>No API Providers</CardTitle>
                <CardDescription>
                  You haven't added any API providers yet.
                </CardDescription>
              </CardHeader>
              <CardFooter>
                <Button onClick={handleAddProvider} variant="outline">
                  <Plus className="mr-2 h-4 w-4" />
                  Add your first provider
                </Button>
              </CardFooter>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {providers.map((provider) => (
                <Card key={provider.id}>
                  <CardHeader className="pb-3">
                    <div className="flex justify-between items-start">
                      <CardTitle className="text-lg">{provider.name}</CardTitle>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleEditProvider(provider)}>
                            <Pencil className="mr-2 h-4 w-4" /> Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleManageEndpoints(provider.id)}>
                            <ListTree className="mr-2 h-4 w-4" /> Manage Endpoints
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    <CardDescription>
                      {provider.code}
                      {provider.is_default && (
                        <Badge className="ml-2" variant="outline">Default</Badge>
                      )}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="pb-2">
                    <p className="text-sm text-muted-foreground">
                      Base URL: <span className="font-mono">{provider.base_url}</span>
                    </p>
                    <div className="flex flex-wrap gap-2 mt-2">
                      <Badge variant="secondary">{provider.auth_type}</Badge>
                      {provider.is_active ? (
                        <Badge variant="outline" className="bg-green-100 text-green-800 border-green-200">Active</Badge>
                      ) : (
                        <Badge variant="outline" className="bg-gray-100 text-gray-800">Inactive</Badge>
                      )}
                    </div>
                  </CardContent>
                  <CardFooter>
                    <Button 
                      variant="outline" 
                      className="w-full"
                      onClick={() => handleManageEndpoints(provider.id)}
                    >
                      <ListTree className="mr-2 h-4 w-4" />
                      Manage Endpoints
                    </Button>
                  </CardFooter>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
        
        <TabsContent value="add">
          <Card>
            <CardHeader>
              <CardTitle>Add API Provider</CardTitle>
              <CardDescription>
                Configure a new API integration provider
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ProviderForm onSuccess={() => setActiveTab('list')} />
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="endpoints">
          {selectedProviderId && <EndpointManager providerId={selectedProviderId} />}
        </TabsContent>
      </Tabs>
      
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="sm:max-w-[700px]">
          <DialogHeader>
            <DialogTitle>
              {selectedProvider ? 'Edit Provider' : 'Add Provider'}
            </DialogTitle>
          </DialogHeader>
          <ProviderForm 
            provider={selectedProvider} 
            onSuccess={handleCloseDialog}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
